import { Injectable, PLATFORM_ID, inject, signal, OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type Theme = 'light' | 'dark';

@Injectable({
  providedIn: 'root'
})
export class ThemeService implements OnDestroy {
  private platformId = inject(PLATFORM_ID);

  // Create signals for theme state
  private _theme = signal<Theme>('light');
  private _systemPrefersDark = signal<boolean>(false);
  private _useSystemPreference = signal<boolean>(true);

  // Expose readonly signals
  public readonly theme = this._theme.asReadonly();
  public readonly systemPrefersDark = this._systemPrefersDark.asReadonly();
  public readonly useSystemPreference = this._useSystemPreference.asReadonly();

  // Media query for detecting system preference
  private darkModeMediaQuery: MediaQueryList | null = null;
  private changeListener: ((e: MediaQueryListEvent) => void) | null = null;

  constructor() {
    this.initializeTheme();
  }

  private initializeTheme(): void {
    // Only access window/document when in browser environment
    if (isPlatformBrowser(this.platformId)) {
      // Initialize the media query
      this.darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      // Set initial value based on system preference
      this._systemPrefersDark.set(this.darkModeMediaQuery.matches);

      // Load saved preferences from localStorage
      const savedTheme = localStorage.getItem('theme') as Theme | null;
      const savedUseSystemPreference = localStorage.getItem('useSystemPreference');

      if (savedUseSystemPreference !== null) {
        this._useSystemPreference.set(savedUseSystemPreference === 'true');
      }

      if (this._useSystemPreference()) {
        // Use system preference
        this._theme.set(this._systemPrefersDark() ? 'dark' : 'light');
      } else if (savedTheme) {
        // Use saved theme
        this._theme.set(savedTheme);
      }

      // Listen for changes in system preference
      if (this.darkModeMediaQuery) {
        this.changeListener = (e) => {
          this._systemPrefersDark.set(e.matches);

          // Update theme if using system preference
          if (this._useSystemPreference()) {
            this._theme.set(e.matches ? 'dark' : 'light');
          }
        };
        this.darkModeMediaQuery.addEventListener('change', this.changeListener);
      }

      // Apply the theme to the document
      this.applyTheme();
    }
  }

  ngOnDestroy(): void {
    if (this.darkModeMediaQuery && this.changeListener) {
      this.darkModeMediaQuery.removeEventListener('change', this.changeListener);
    }
  }

  // Toggle between light and dark mode
  public toggleTheme(): void {
    // Disable system preference when manually toggling
    this._useSystemPreference.set(false);
    this._theme.update(current => current === 'light' ? 'dark' : 'light');
    this.applyTheme();
  }

  // Set a specific theme
  public setTheme(theme: Theme): void {
    this._useSystemPreference.set(false);
    this._theme.set(theme);
    this.applyTheme();
  }

  // Enable/disable system preference
  public setUseSystemPreference(useSystem: boolean): void {
    this._useSystemPreference.set(useSystem);

    if (useSystem && isPlatformBrowser(this.platformId)) {
      // Update theme based on current system preference
      this._theme.set(this._systemPrefersDark() ? 'dark' : 'light');
      this.applyTheme();
    }
  }

  // Apply the current theme to the document
  private applyTheme(): void {
    if (isPlatformBrowser(this.platformId)) {
      const isDark = this._theme() === 'dark';

      // Apply dark mode class to document element
      if (isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }

      // Update the Material prebuilt theme link
      let themeLink = document.getElementById('material-theme') as HTMLLinkElement;
      if (!themeLink) {
        themeLink = document.createElement('link');
        themeLink.id = 'material-theme';
        themeLink.rel = 'stylesheet';
        document.head.appendChild(themeLink);
      }
      themeLink.href = isDark ? 'theme-dark.css' : 'theme-light.css';

      // Store preference in localStorage for persistence
      localStorage.setItem('theme', this._theme());
      localStorage.setItem('useSystemPreference', String(this._useSystemPreference()));
    }
  }
}
